import type { GetMaintenanceResponse } from "@api/app-api";
import RefreshIcon from "@mui/icons-material/Refresh";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Paper,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import React, { useCallback, useEffect, useState } from "react";
import { apiClient } from "../../api";
import { environmentLabels, type Environment } from "../../config";

interface MaintenancePageProps {
  environment: Environment;
}

/** datetime-local wants "YYYY-MM-DDTHH:mm" in LOCAL time; the stored value is UTC ISO. */
function isoToLocalInput(iso: string | undefined): string {
  if (!iso) return "";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  const offset = date.getTimezoneOffset() * 60_000;
  return new Date(date.getTime() - offset).toISOString().slice(0, 16);
}

function localInputToIso(value: string): string | null {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date.toISOString();
}

export const MaintenancePage: React.FC<MaintenancePageProps> = ({
  environment,
}) => {
  const [enabled, setEnabled] = useState(false);
  const [message, setMessage] = useState("");
  const [until, setUntil] = useState("");
  const [bypassUserIds, setBypassUserIds] = useState("");
  const [saved, setSaved] = useState<GetMaintenanceResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const applyState = useCallback((state: GetMaintenanceResponse) => {
    setSaved(state);
    setEnabled(state.enabled);
    setMessage(state.message);
    setUntil(isoToLocalInput(state.until));
    setBypassUserIds(state.bypassUserIds.join("\n"));
  }, []);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    apiClient
      .getMaintenance()
      .then(applyState)
      .catch((err: unknown) =>
        setError(err instanceof Error ? err.message : String(err)),
      )
      .finally(() => setLoading(false));
  }, [applyState]);

  useEffect(load, [load, environment]);

  const save = () => {
    setSaving(true);
    setError(null);
    setNotice(null);
    apiClient
      .setMaintenance({
        enabled,
        message,
        until: localInputToIso(until),
        bypassUserIds: bypassUserIds
          .split("\n")
          .map((id) => id.trim())
          .filter(Boolean),
      })
      .then((response) => {
        applyState(response.state);
        setNotice(`${response.message} — takes effect within ~30 seconds.`);
      })
      .catch((err: unknown) =>
        setError(err instanceof Error ? err.message : String(err)),
      )
      .finally(() => setSaving(false));
  };

  const requestSave = () => {
    // Only the switch-on needs confirming, and only where it locks out real customers.
    if (enabled && !saved?.enabled && environment === "prod") {
      setConfirmOpen(true);
      return;
    }
    save();
  };

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 2,
        }}
      >
        <Box>
          <Typography variant="h5">Maintenance Mode</Typography>
          <Typography variant="body2" color="text.secondary">
            Locks every API route in {environmentLabels[environment].long}.
            Users can still sign in, then land on the maintenance page.
          </Typography>
        </Box>
        <Button
          startIcon={<RefreshIcon />}
          onClick={load}
          disabled={loading || saving}
        >
          Reload
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {notice && (
        <Alert
          severity="success"
          sx={{ mb: 2 }}
          onClose={() => setNotice(null)}
        >
          {notice}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", my: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Paper sx={{ p: 3 }}>
          <Stack spacing={3}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Switch
                checked={enabled}
                onChange={(event) => setEnabled(event.target.checked)}
              />
              <Chip
                label={saved?.enabled ? "Currently ON" : "Currently OFF"}
                color={saved?.enabled ? "error" : "success"}
                size="small"
              />
              {saved?.updatedBy && (
                <Typography variant="caption" color="text.secondary">
                  last changed by {saved.updatedBy}
                  {saved.updatedAt
                    ? ` on ${new Date(saved.updatedAt).toLocaleString()}`
                    : ""}
                </Typography>
              )}
            </Box>

            <TextField
              label="Message shown to users"
              value={message}
              onChange={(event) => setMessage(event.target.value)}
              multiline
              minRows={3}
              fullWidth
              inputProps={{ maxLength: 2000 }}
              helperText="Shown as typed, in every language. Leave empty for the built-in default text."
            />

            <TextField
              label="Expected back at (optional)"
              type="datetime-local"
              value={until}
              onChange={(event) => setUntil(event.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ maxWidth: 320 }}
              helperText="Your local time; stored as UTC."
            />

            <TextField
              label="Bypass user IDs (optional)"
              value={bypassUserIds}
              onChange={(event) => setBypassUserIds(event.target.value)}
              multiline
              minRows={2}
              fullWidth
              helperText="One Cognito sub per line. These users keep working while the gate is on — smoke-test a locked stage."
            />

            <Box>
              <Button
                variant="contained"
                onClick={requestSave}
                disabled={saving}
                color={enabled ? "error" : "primary"}
              >
                {saving ? "Saving…" : enabled ? "Enable maintenance" : "Save"}
              </Button>
            </Box>
          </Stack>
        </Paper>
      )}

      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle>Lock out Production?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Every API route in Production will return 503 within ~30 seconds.
            All users land on the maintenance page until you switch this off.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            onClick={() => {
              setConfirmOpen(false);
              save();
            }}
          >
            Enable maintenance
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
